/**
 * Retry con exponential backoff per operazioni di rete.
 */

export interface RetryConfig {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  retryableErrors: string[];
}

const DEFAULT_CONFIG: RetryConfig = {
  maxAttempts: 5,
  initialDelayMs: 1000,
  maxDelayMs: 300000,
  backoffMultiplier: 2,
  retryableErrors: [
    'NETWORK_ERROR',
    'TIMEOUT',
    'SERVICE_UNAVAILABLE',
    'FAILED TO FETCH',
    'NETWORKREQUESTFAILED',
    '503',
    '502',
    '504',
  ],
};

class RetryService {
  async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const cfg = { ...DEFAULT_CONFIG, ...config };
    let lastError: Error | null = null;
    let delay = cfg.initialDelayMs;

    for (let attempt = 1; attempt <= cfg.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (!this._isRetryable(lastError, cfg.retryableErrors)) {
          throw lastError;
        }

        if (attempt === cfg.maxAttempts) {
          break;
        }

        await this._sleep(delay);
        delay = Math.min(delay * cfg.backoffMultiplier, cfg.maxDelayMs);
      }
    }

    throw lastError;
  }

  private _isRetryable(error: Error, retryableErrors: string[]): boolean {
    const errorMessage = error?.message ?? '';
    const errorCode = (error as Error & { code?: string })?.code ?? '';
    const statusCode = (error as Error & { response?: { status?: number } })?.response?.status?.toString() ?? '';

    return retryableErrors.some(
      (re) =>
        errorMessage.toUpperCase().includes(re) ||
        errorCode.toUpperCase().includes(re) ||
        statusCode === re
    );
  }

  private _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const retryService = new RetryService();
